import pptxgen from "pptxgenjs";
import { StackedVerticalBarChartOptionsNew } from "./ppt.options";
import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class DownloadEngagementDashboard {
  public errorWhilegenerating = false;

  public engagementTypes = [
    { text: "ENGAGED", color: "f58026" },
    { text: "ALMOST ENGAGED", color: "578bc9" },
    { text: "CRASH & BURN", color: "986142" },
    { text: "HONEYMOONERS & HAMSTERS", color: "ffc305" },
    { text: "DISENGAGED", color: "7f7f7f" },
  ];

  // Number of data rows to show per table slide before splitting into a new one
  private readonly TABLE_ROWS_PER_PAGE = 10;

  public async downloadEngagementDashboardReport(
    reports: any[],
    otherFilters: string,
    clientLogo: string,
  ) {
    try {
      let pptx = new pptxgen();

      let clientLogoBase64 = "";

      try {
        if (clientLogo) {
          clientLogoBase64 = await this.urlToBase64(clientLogo);
        }
      } catch (err) {
        console.error("Failed to load client logo", err);
      }

      // SAFETY CHECK
      if (!reports || reports.length === 0) {
        return false;
      }

      // ─────────────────────────────────────────────
      // SLIDE 1: OVERALL X-MODEL + ENGAGEMENT SCORE
      // ─────────────────────────────────────────────

      const firstReport = reports[0];
      const totalEngagement = firstReport.totalEngagement;

      let slide = pptx.addSlide();

      // SLIDE BACKGROUND
      slide.addShape(pptx.ShapeType.rect, {
        x: 0,
        y: 0,
        w: "100%",
        h: "100%",
        fill: { color: "FFFFFF" },
        line: { type: "none" },
      });

      // TOP HEADER BAR
      slide.addShape(pptx.ShapeType.rect, {
        x: 0,
        y: 0,
        w: "100%",
        h: 0.75,
        fill: { color: "0757A7" },
        line: { type: "none" },
      });

      // HRA LOGO left
      slide.addImage({
        path: "assets/hra-logo.png",
        x: 0.15,
        y: 0.1,
        w: 1.2,
        h: 0.5,
      });

      // SURVEY NAME - centered in header
      slide.addText(`SURVEY : ${firstReport.surveyName} - OVERALL`, {
        x: 1.8,
        y: 0.1,
        w: 6.2,
        h: 0.55,
        fontSize: 15,
        bold: true,
        fontFace: "Poppins",
        color: "FFFFFF",
        valign: "middle",
        align: "center",
      });

      // CLIENT LOGO right
      if (clientLogoBase64) {
        slide.addImage({
          data: clientLogoBase64,
          x: 8.45,
          y: 0.1,
          w: 1.2,
          h: 0.5,
        });
      }

      // X-MODEL IMAGE
      slide.addImage({
        path: "assets/X-Model_Image-new.jpg",
        x: 0.7,
        y: 1.35,
        w: 3.8,
        h: 3.6,
      });

      // X-Model overlay values
      const leftPanelText = [
        { key: "fullyEngaged", x: 2.38, y: 2.32 },
        { key: "almostEngaged", x: 2.34, y: 2.82 },
        { key: "honeymoonHamsters", x: 1.7, y: 3.2 },
        { key: "crashBurners", x: 3.05, y: 3.15 },
        { key: "disengaged", x: 2.325, y: 3.82 },
      ];

      leftPanelText.forEach((item) => {
        slide.addText(`${totalEngagement[item.key]}`, {
          x: item.x,
          y: item.y,
          fontFace: "Poppins",
          fontSize: 9,
          bold: true,
          color: "FFFFFF",
        });
      });

      // RIGHT PANEL CARD
      slide.addShape(pptx.ShapeType.rect, {
        x: 5.6,
        y: 1.2,
        w: 3.9,
        h: 3.58,
        fill: { color: "FFFFFF" },
        line: { color: "C8D8F0", width: 1 },
      });

      // Panel header bg
      slide.addShape(pptx.ShapeType.rect, {
        x: 5.6,
        y: 1.2,
        w: 3.9,
        h: 1.15,
        fill: { color: "C5D8F5" },
        line: { type: "none" },
      });

      slide.addText("Engagement Score", {
        x: 5.9,
        y: 1.28,
        w: 3.9,
        h: 0.48,
        fontSize: 16,
        bold: true,
        fontFace: "Poppins",
        color: "1A3C6E",
        valign: "middle",
      });

      // Total Responses — label + value inline
      slide.addText("Total Responses : ", {
        x: 5.9,
        y: 1.88,
        fontFace: "Poppins",
        fontSize: 12,
        color: "555555",
      });

      slide.addText(`${totalEngagement.total}`, {
        x: 7.35,
        y: 1.86,
        fontFace: "Poppins",
        fontSize: 20,
        bold: true,
        color: "0d8f1c",
      });

      // ENGAGEMENT ROWS (alternating bg)
      const rowItems = [
        { label: "Engaged :", key: "fullyEngaged", color: "FF8404" },
        { label: "Almost engaged :", key: "almostEngaged", color: "588CCC" },
        {
          label: "Honeymooners & hamsters :",
          key: "honeymoonHamsters",
          color: "ECBC0C",
        },
        { label: "Crash & burn :", key: "crashBurners", color: "A06444" },
        { label: "Disengaged :", key: "disengaged", color: "807C7C" },
      ];

      const rowStartY = 2.28;
      const rowH = 0.5;
      const evenBg = "D6E4F7";
      const oddBg = "EEF4FC";

      rowItems.forEach((item, i) => {
        const rowY = rowStartY + i * rowH;
        const bgCol = i % 2 === 0 ? evenBg : oddBg;

        // Row background
        slide.addShape(pptx.ShapeType.rect, {
          x: 5.6,
          y: rowY,
          w: 3.9,
          h: rowH,
          fill: { color: bgCol },
          line: { type: "none" },
        });

        // Label — italic, left
        slide.addText(item.label, {
          x: 5.92,
          y: rowY + 0.06,
          w: 2.6,
          h: rowH - 0.1,
          fontSize: 12,
          italic: true,
          fontFace: "Poppins",
          color: "333333",
          valign: "middle",
        });

        // Value — bold, colored, right-aligned
        slide.addText(`${totalEngagement[item.key]}`, {
          x: 8.35,
          y: rowY + 0.06,
          w: 1.0,
          h: rowH - 0.1,
          fontSize: 14,
          bold: true,
          fontFace: "Poppins",
          color: item.color,
          align: "right",
          valign: "middle",
        });
      });

      this.addBottomEngagementRow(slide);

      // ─────────────────────────────────────────────
      // SLIDES 2+: BAR CHART + TABLE PER FILTER
      // ─────────────────────────────────────────────

      reports.forEach((report) => {
        if (!report.engagementDatas || report.engagementDatas.length === 0) {
          return;
        }

        if (!report.engagementBars || report.engagementBars.length === 0) {
          console.warn(
            `No engagementBars for filter: ${report.header.filter}`,
          );
          return;
        }

        // ─────────────────────────────────────────────
        // BAR CHART SLIDE
        // ─────────────────────────────────────────────

        slide = pptx.addSlide();

        // BACKGROUND
        slide.addShape(pptx.ShapeType.rect, {
          x: 0,
          y: 0,
          w: "100%",
          h: "100%",
          fill: { color: "FFFFFF" },
          line: { type: "none" },
        });

        // HEADER BAR
        slide.addShape(pptx.ShapeType.rect, {
          x: 0,
          y: 0,
          w: "100%",
          h: 0.75,
          fill: { color: "0757A7" },
          line: { type: "none" },
        });

        slide.addImage({
          path: "assets/hra-logo.png",
          x: 0.15,
          y: 0.1,
          w: 1.2,
          h: 0.5,
        });

        slide.addText(
          `SURVEY : ${report.surveyName} - ${report.header.filter}`,
          {
            x: 1.8,
            y: 0.1,
            w: 6.2,
            h: 0.55,
            fontSize: 15,
            bold: true,
            fontFace: "Poppins",
            color: "FFFFFF",
            valign: "middle",
            align: "center",
          },
        );

        if (clientLogoBase64) {
          slide.addImage({
            data: clientLogoBase64,
            x: 8.45,
            y: 0.1,
            w: 1.2,
            h: 0.5,
          });
        }

        const engagementConfig = [
          { label: "Engaged", key: "fullyEngaged", color: "FF8404" },
          { label: "Almost Engaged", key: "almostEngaged", color: "588CCC" },
          {
            label: "Honeymooners & Hamsters",
            key: "honeymoonHamsters",
            color: "ECBC0C",
          },
          { label: "Crash & Burn", key: "crashBurners", color: "A06444" },
          { label: "Disengaged", key: "disengaged", color: "807C7C" },
        ];

        // Bar chart now sources from "engagementBars" (pre-aggregated/trimmed
        // set from the API) instead of the full "engagementDatas" list.
        // const labels = report.engagementDatas.map((d) => d.filter);
        const labels = report.engagementBars.map((d) => {
          let label = d.filter || "";

          // Break after comma
          label = label.replace(/,\s*/g, ",\n");

          return label;
        });

        const deptData =
          report.departmentCounts ||
          report.engagementBars.map((d) => ({
            name: d.filter,
            count: d.total || d.sampleSize || "",
          }));

        const reversedConfig = [...engagementConfig].reverse();

        const chartData = reversedConfig.map((cfg) => ({
          name: cfg.label,
          labels: labels,
          values: report.engagementBars.map((d) => {
            const raw = d[cfg.key];

            if (!raw || raw === "NaN%") return null;

            const num = parseFloat(raw.toString().replace("%", ""));

            if (isNaN(num) || num <= 0) return null;

            return num / 100;
          }),
        }));

        let chartOptions = JSON.parse(
          JSON.stringify(StackedVerticalBarChartOptionsNew),
        );

        chartOptions.chartColors = reversedConfig.map((c) => c.color);
        chartOptions.barGrouping = "percentStacked";
        chartOptions.valAxisMinVal = 0;
        chartOptions.valAxisMaxVal = 1;
        chartOptions.valAxisMajorUnit = 0.1;
        chartOptions.valAxisLabelFormatCode = "0%";
        chartOptions.dataLabelFormatCode = "0%";
        chartOptions.barGapWidthPct = 100;
        chartOptions.barOverlapPct = 100;
        chartOptions.x = 0.25;
        chartOptions.y = 1.0;
        chartOptions.w = 5.9;
        chartOptions.h = 3.5;
        chartOptions.fontFace = "Poppins";
        chartOptions.catAxisLabelFontFace = "Poppins";
        chartOptions.valAxisLabelFontFace = "Poppins";
        chartOptions.legendFontFace = "Poppins";
        chartOptions.dataLabelFontFace = "Poppins";
        chartOptions.catAxisLabelFontSize = 8;

        slide.addChart(pptx.ChartType.bar, chartData, chartOptions);

        // COUNTS ABOVE EACH BAR
        const axisOffset = 0.7;
        const barAreaX = chartOptions.x + axisOffset;
        const barAreaW = chartOptions.w - axisOffset;
        const barCount = labels.length || 1;
        const barSlotW = barAreaW / barCount;

        labels.forEach((label, index) => {
          const count = deptData[index].count || "";

          const barCenterX = barAreaX + index * barSlotW + barSlotW / 2;

          slide.addText(`${count}`, {
            x: barCenterX - 0.45,
            y: 0.8,
            w: 0.7,
            h: 0.25,
            fontSize: 12,
            fontFace: "Poppins",
            bold: true,
            color: "000000",
            align: "center",
          });
        });

        // X-MODEL IMAGE (bar chart slide)
        const xModelText = [
          { key: "fullyEngaged", x: 7.8, y: 1.91 },
          { key: "almostEngaged", x: 7.745, y: 2.36 },
          { key: "honeymoonHamsters", x: 7.25, y: 2.77 },
          { key: "crashBurners", x: 8.4, y: 2.7 },
          { key: "disengaged", x: 7.75, y: 3.3 },
        ];

        slide.addImage({
          path: "assets/X-Model_Image-new.jpg",
          x: 6.3,
          y: 1,
          w: 3.4,
          h: 3.4,
        });

        xModelText.forEach((item) => {
          slide.addText(`${report.totalEngagement[item.key]}`, {
            x: item.x,
            y: item.y,
            fontFace: "Poppins",
            fontSize: 9,
            bold: true,
            color: "FFFFFF",
          });
        });

        // OVERALL ENGAGEMENT SCORE BOX
        slide.addShape(pptx.ShapeType.rect, {
          x: 7.15,
          y: 4.45,
          w: 1.8,
          h: 0.42,
          fill: { color: "E7A977" },
          line: { type: "none" },
        });

        slide.addText(
          `Overall Engagement Score\nSample Size - ${report.totalEngagement.total}`,
          {
            x: 7.15,
            y: 4.5,
            w: 1.8,
            h: 0.32,
            fontSize: 8,
            fontFace: "Poppins",
            bold: true,
            color: "3A2A1F",
            align: "center",
            margin: 0,
          },
        );

        // DEPT LABELS BELOW CHART
        // const deptPerRow = 3;
        // const deptStartX = 0.1;
        // const deptSpacing = (5.9 - (deptStartX - 0.5)) / deptPerRow;

        // let xPos = deptStartX;
        // let yPos = 4.5;

        // deptData.forEach((dept, index) => {
        //   slide.addText(`${dept.name} : ${dept.count}`, {
        //     x: xPos,
        //     y: yPos,
        //     w: deptSpacing,
        //     align: "center",
        //     fontSize: 11,
        //     fontFace: "Poppins",
        //     bold: true,
        //     color: "000000",
        //   });

        //   xPos += deptSpacing;

        //   if ((index + 1) % deptPerRow === 0) {
        //     xPos = deptStartX;
        //     yPos += 0.25;
        //   }
        // });

        // Check whether current slide's chart data contains "Others"
        const hasOthers = report.engagementBars.some(
          (item) => item.filter.trim().toLowerCase() === "others",
        );

        if (hasOthers && otherFilters) {
          const currentFilter = report.header.filter.trim().toLowerCase();

          const filterNote = otherFilters.split("||").find((item) => {
            const key = item.split(":")[0].trim().toLowerCase();
            return key === currentFilter;
          });

          if (filterNote) {
            const parts = filterNote.split(":");

            const key = parts[0].trim();
            const value = parts.slice(1).join(":").trim();

            slide.addText(`Note: Other ${key} consists of - ${value}`, {
              x: 0.4,
              y: 4.7,
              w: 6.9,
              h: 0.3,
              fontSize: 8,
              fontFace: "Poppins",
              color: "F28C00",
              bold: true,
            });
          }
        }
        this.addBottomEngagementRow(slide);

        // ─────────────────────────────────────────────
        // TABLE SLIDE(S) (same data, table format, paginated)
        // Table still uses the full "engagementDatas" list.
        // ─────────────────────────────────────────────

        const dataRows = report.engagementDatas;
        const rowsPerPage = this.TABLE_ROWS_PER_PAGE;
        const totalPages = Math.max(1, Math.ceil(dataRows.length / rowsPerPage));

        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
          const pageRows = dataRows.slice(
            pageIndex * rowsPerPage,
            (pageIndex + 1) * rowsPerPage,
          );

          let tableSlide = pptx.addSlide();

          // BACKGROUND
          tableSlide.addShape(pptx.ShapeType.rect, {
            x: 0,
            y: 0,
            w: "100%",
            h: "100%",
            fill: { color: "FFFFFF" },
            line: { type: "none" },
          });

          // HEADER BAR
          tableSlide.addShape(pptx.ShapeType.rect, {
            x: 0,
            y: 0,
            w: "100%",
            h: 0.75,
            fill: { color: "0757A7" },
            line: { type: "none" },
          });

          tableSlide.addImage({
            path: "assets/hra-logo.png",
            x: 0.15,
            y: 0.1,
            w: 1.2,
            h: 0.5,
          });

          // const pageSuffix = totalPages > 1 ? ` (Page ${pageIndex + 1}/${totalPages})` : "";

          tableSlide.addText(
            `SURVEY : ${report.surveyName} - ${report.header.filter}`,
            {
              x: 1.8,
              y: 0.1,
              w: 6.2,
              h: 0.55,
              fontSize: 15,
              bold: true,
              fontFace: "Poppins",
              color: "FFFFFF",
              valign: "middle",
              align: "center",
            },
          );

          if (clientLogoBase64) {
            tableSlide.addImage({
              data: clientLogoBase64,
              x: 8.45,
              y: 0.1,
              w: 1.2,
              h: 0.5,
            });
          }

          // TABLE HEADER HELPER
          const tableHeaderOpts = (bgColor: string): any => ({
            bold: true,
            color: "FFFFFF",
            fill: bgColor, // ← plain hex string
            align: "center",
            valign: "middle",
            fontSize: 7,
            fontFace: "Poppins",
          });

          const tblRows: any[] = [];

          // HEADER ROW (repeated on every page)
          tblRows.push([
            {
              text: `${report.header.filter}`,
              options: tableHeaderOpts("70AD47"),
            },
            { text: "Sample\nSize", options: tableHeaderOpts("70AD47") },
            { text: "Fully\nEngaged", options: tableHeaderOpts("FF8404") },
            { text: "Almost\nEngaged", options: tableHeaderOpts("588CCC") },
            { text: "Crash &\nBurn", options: tableHeaderOpts("A06444") },
            {
              text: "Honeymooners &\nHamsters",
              options: tableHeaderOpts("ECBC0C"),
            },
            { text: "Disengaged", options: tableHeaderOpts("807C7C") },
          ]);

          // DATA ROWS (only this page's slice)
          pageRows.forEach((row: any, index: number) => {
            const rowBg = index % 2 === 0 ? "D5E3CF" : "EBF1E9";

            const baseOpts: any = {
              align: "center",
              valign: "middle",
              fontSize: 7,
              fontFace: "Poppins",
              fill: rowBg, // ← plain hex string
            };

            // Cell bg = column color, text = white
            const colorVal = (val: any, color: string): any => ({
              text: val.toString() || "0%",
              options: {
                align: "center",
                valign: "middle",
                fontSize: 7,
                fontFace: "Poppins",
                bold: true,
                color: "FFFFFF",
                fill: color, // ← plain hex string for pptxgenjs 3.1.1
              },
            });

            tblRows.push([
              {
                text: row.filter,
                options: {
                  ...baseOpts,
                  italic: true,
                  align: "center",
                  color: "333333",
                },
              },
              {
                text: (row.total || row.sampleSize || "0").toString(),
                options: { ...baseOpts, color: "333333" },
              },
              colorVal(row.fullyEngaged, "FF8404"),
              colorVal(row.almostEngaged, "588CCC"),
              colorVal(row.crashBurners, "A06444"),
              colorVal(row.honeymoonHamsters, "ECBC0C"),
              colorVal(row.disengaged, "807C7C"),
            ]);
          });

          tableSlide.addTable(tblRows, {
            x: 0.2,
            y: 0.85,
            w: 6.0,
            colW: [1, 0.7, 0.75, 0.75, 0.75, 0.9, 0.75],
            rowH: 0.34,
            border: { type: "solid", pt: 1, color: "FFFFFF" },
            fontSize: 7,
            fontFace: "Poppins",
          });

          // X-MODEL IMAGE (table slide)
          tableSlide.addImage({
            path: "assets/X-Model_Image-new.jpg",
            x: 6.3,
            y: 1,
            w: 3.4,
            h: 3.4,
          });

          xModelText.forEach((item) => {
            tableSlide.addText(`${report.totalEngagement[item.key]}`, {
              x: item.x,
              y: item.y,
              fontFace: "Poppins",
              fontSize: 9,
              bold: true,
              color: "FFFFFF",
            });
          });

          // OVERALL ENGAGEMENT SCORE BOX (table slide)
          tableSlide.addShape(pptx.ShapeType.rect, {
            x: 7.15,
            y: 4.45,
            w: 1.8,
            h: 0.42,
            fill: { color: "E7A977" },
            line: { type: "none" },
          });

          tableSlide.addText(
            `Overall Engagement Score\nSample Size - ${report.totalEngagement.total}`,
            {
              x: 7.15,
              y: 4.5,
              w: 1.8,
              h: 0.32,
              fontSize: 8,
              fontFace: "Poppins",
              bold: true,
              color: "3A2A1F",
              align: "center",
              margin: 0,
            },
          );

          // Check whether THIS PAGE contains "Others" (not the whole report)
          const hasOthersOnThisPage = pageRows.some(
            (item) => item.filter.trim().toLowerCase() === "others",
          );

          if (hasOthersOnThisPage && otherFilters) {
            const currentFilter = report.header.filter.trim().toLowerCase();

            const filterNote = otherFilters.split("||").find((item) => {
              const key = item.split(":")[0].trim().toLowerCase();
              return key === currentFilter;
            });

            if (filterNote) {
              const parts = filterNote.split(":");

              const key = parts[0].trim();
              const value = parts.slice(1).join(":").trim();

              tableSlide.addText(`Note: Other ${key} consists of - ${value}`, {
                x: 0.4,
                y: 4.7,
                w: 6.9,
                h: 0.3,
                fontSize: 8,
                fontFace: "Poppins",
                color: "F28C00",
                bold: true,
              });
            }
          }

          this.addBottomEngagementRow(tableSlide);
        }
      });

      const filterNames = reports
        .map((r) => r.header.filter)
        .filter(Boolean)
        .join(", ");

      const fileName = `${reports[0].surveyName || "Survey"} - ${filterNames} - Dashboard Report`;

      pptx.writeFile(fileName + ".pptx");

      return this.errorWhilegenerating;
    } catch (error) {
      console.error(error);

      this.errorWhilegenerating = true;

      return this.errorWhilegenerating;
    }
  }

  public addBottomEngagementRow(slide) {
    let allRows = [];
    let bottomRow = [];

    this.engagementTypes.forEach((engagement) => {
      bottomRow.push({
        text: engagement.text,
        options: {
          valign: "middle",
          align: "center",
          fontSize: 7,
          fontFace: "Poppins",
          color: "FFFFFF",
          fill: engagement.color,
        },
      });
    });

    allRows.push(bottomRow);

    slide.addTable(allRows, {
      x: 0,
      y: 5.37,
      w: 10.0,
      h: 0.25,
      border: { type: "none" },
    });

    return slide;
  }

  private async urlToBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        resolve(reader.result as string);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}